import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  CustomSettings,
  getDefaultSectionOrderForTemplateType,
  isInvoiceOrProformaTemplateType,
  isProformaInvoiceTemplateType,
} from "subModule/src/services/invoice/types";
import dayjs from "dayjs";
import { useUserLanguages } from "./hooks/useUserLanguages";
import { useDefaultLabels } from "./hooks/useDefaultLabels";
import ThermalInvoiceContent, {
  ThermalInvoiceData,
} from "./thermal/ThermalInvoiceContent";
import { isThermalPrint } from "./thermal/thermalInvoiceUtils";
import ModernInvoicePreview from "./ModernInvoicePreview";
import { brandingInfo } from "features/server/brandingSlice";
import { getBrandingAboutHref } from "Helpers/brandingFooter";

interface InvoicePreviewProps {
  settings: CustomSettings;
  theme: any;
  formData: any;
  templateType?: number; // INVOICE = 2, JOBCARD = 3, WORKORDER = 4, INSPECTION = 5, DELIVERY_NOTE = 6, PROFORMA_INVOICE = 7
}

const InvoicePreview: React.FC<InvoicePreviewProps> = ({
  settings,
  theme,
  formData,
  templateType = 2,
}) => {
  const isProforma = isProformaInvoiceTemplateType(templateType);
  const { t } = useTranslation();
  const { secondaryLanguage } = useUserLanguages();
  const branding = useSelector(brandingInfo);
  const {
    // getCompanyLabel,
    // getCustomerLabel,
    // getVehicleLabel,
    getTableColumnLabel,
    getConsolidatedTableColumnLabel,
    getSummaryLabel,
    getPaymentLabel,
    getTableSectionLabel,
    getTermsLabel,
    getFooterLabel,
  } = useDefaultLabels();

  // Dummy data for preview
  // Generate invoice number based on template type
  const getInvoiceNumber = () => {
    const dateStr = "2025-10-1410";
    if (templateType === 3) {
      return `JC-${dateStr}`; // JOBCARD
    } else if (templateType === 4) {
      return `WO-${dateStr}`; // WORKORDER
    } else if (templateType === 5) {
      return t("inspectionForm");
    } else if (templateType === 6) {
      return `DN-${dateStr}`; // DELIVERY NOTE
    } else if (isProforma) {
      return `P-INV-${dateStr}`;
    }
    return `INV-${dateStr}`; // INVOICE (default)
  };

  const dummyData = {
    invoiceNo: getInvoiceNumber(),
    date: new Date().toISOString(),
    company: {
      name: "Demo Auto Services Ltd",
      phone: "03001000000",
      email: "info@demoautoservices.com",
      address: "Suite 101, Demo Plaza, Test Road, Sample City 00000",
      vat: "VAT123456",
    },
    customer: {
      name: "John Doe",
      phone: "03111111111",
      email: "john.doe@example.com",
      address: "House 12, Test Street, Sample Area",
      vat: "VAT789012",
    },
    vehicle: {
      make: "Toyota",
      model: "Carola",
      fuelType: "PETROL",
      mileage: "15000",
      vin: "VIN-TEST-000123456",
    },
    // Dummy inspection items
    inspectionItems: [
      { id: "1", partName: "Part 1", status: "needRepair" as const, notes: "" },
      {
        id: "2",
        partName: "Part 2",
        status: "brokenOrMissing" as const,
        notes: "",
      },
      { id: "3", partName: "Part 3", status: "working" as const, notes: "" },
    ],
    technicians: ["Technician 1", "Technician 2", "Technician 3"],
    items: [
      {
        type: "package",
        title: "Package 1",
        code: "PKG1",
        description:
          "Complete maintenance package including oil change and inspection",
        quantity: 1,
        unitPrice: 500.0,
        discount: 0.0,
        tax: 50.0,
        amount: 500.0,
      },
      {
        type: "service",
        title: "Service 2",
        code: "Ser2",
        description: "Brake pad replacement service",
        quantity: 1,
        unitPrice: 400.4,
        discount: 0.0,
        tax: 30.0,
        amount: 400.4,
      },
      {
        type: "item",
        title: "Frnt right tyre",
        code: "Frnt right tyre",
        description: "Front right tire replacement",
        quantity: 1,
        unitPrice: 300.0,
        discount: 0.0,
        tax: 10.0,
        amount: 300.0,
      },
    ],
    // Consolidated invoice delivery notes data
    deliveryNotes: [
      {
        vehicleName: "Demo-Vehicle-2025",
        vehiclePlateNo: "TEST-1234",
        deliveryNoteNo: "DN-2025-10-5",
        subtotal: 100.0,
        discount: 0.0,
        tax: 15.0,
        total: 115.0,
      },
    ],
    subtotal: 1200.4,
    discount: 0.0,
    tax: 54.02,
    total: 1254.42,
    creditAmount: 0.0,
    paidAmount: 1254.42,
    dueAmount: 0.0,
    paymentMode: "Cash",
    footerText: "This is system generated invoice/JC/WO/Estimation.",
  };

  const renderLanguage = (primary: string, secondary: string) => {
    if (settings.languageDisplay === "primary") return primary;
    if (settings.languageDisplay === "secondary") return secondary;
    return (
      <>
        <span>{primary}</span>
        {secondary && (
          <>
            <br />
            <span
              className="ff-arabic"
              //  style={{ fontSize: "0.9em" }}
            >
              {secondary}
            </span>
          </>
        )}
      </>
    );
  };

  const renderLabel = (primary: string, secondary: string) => {
    if (settings.languageDisplay === "primary") return primary;
    if (settings.languageDisplay === "secondary") return secondary;
    // For "both", show both languages separated by /
    return `${primary} / ${secondary}`;
  };

  const formatDate = (date: string) => {
    const format = settings.dateFormat || "DD-MM-YYYY";
    return dayjs(date).format(format);
  };

  // Helper functions to get dynamic field names based on templateType
  const getShowNumberField = () => {
    if (templateType === 3) return settings.showJobcardNumber;
    if (templateType === 4) return settings.showWorkorderNumber;
    if (templateType === 6) return settings.showDeliveryNoteNumber;
    if (isProforma) return settings.showProformaInvoiceNumber;
    return settings.showInvoiceNumber;
  };

  const getNumberPositionField = () => {
    if (templateType === 3) return settings.jobcardNumberPosition;
    if (templateType === 4) return settings.workorderNumberPosition;
    if (templateType === 6) return settings.deliveryNoteNumberPosition;
    if (isProforma) return settings.proformaInvoiceNumberPosition;
    return settings.invoiceNumberPosition;
  };

  const getNumberFontSizeField = () => {
    if (templateType === 3) return settings.jobcardNumberFontSize;
    if (templateType === 4) return settings.workorderNumberFontSize;
    if (templateType === 6) return settings.deliveryNoteNumberFontSize;
    if (isProforma) return settings.proformaInvoiceNumberFontSize;
    return settings.invoiceNumberFontSize;
  };

  const getNumberFontWeightField = () => {
    if (templateType === 3) return settings.jobcardNumberFontWeight;
    if (templateType === 4) return settings.workorderNumberFontWeight;
    if (templateType === 6) return settings.deliveryNoteNumberFontWeight;
    if (isProforma) return settings.proformaInvoiceNumberFontWeight;
    return settings.invoiceNumberFontWeight;
  };

  const getLabel = (
    field: string,
    type: "company" | "customer" | "vehicle" = "company",
  ) => {
    const labels =
      type === "company"
        ? settings.companyLabels?.[field as keyof typeof settings.companyLabels]
        : type === "customer"
          ? settings.customerLabels?.[
              field as keyof typeof settings.customerLabels
            ]
          : settings.vehicleLabels?.[
              field as keyof typeof settings.vehicleLabels
            ];

    // Get default labels based on type
    // const defaultLabels =
    //   type === "company"
    //     ? getCompanyLabel(field)
    //     : type === "customer"
    //       ? getCustomerLabel(field)
    //       : getVehicleLabel(field);

    // Use custom labels if they exist and are not empty, otherwise use defaults
    return {
      primary:
        labels?.primary && labels.primary.trim() !== "" ? labels.primary : "",
      secondary:
        labels?.secondary && labels.secondary.trim() !== ""
          ? labels.secondary
          : "",
    };
  };

  const renderSection = (sectionId: string) => {
    switch (sectionId) {
      case "logo": {
        // New independent positioning logic
        const logoPosition =
          settings.logoPosition || (settings.showLogo ? "left" : "none");
        const invoiceNumberPosition =
          getNumberPositionField() ||
          (getShowNumberField() !== false ? "center" : "none");
        const qrPosition =
          settings.qrPosition || (settings.showQR ? "right" : "none");

        const showLogo = settings.showLogo && logoPosition !== "none";
        const showInvoiceNumber =
          getShowNumberField() !== false && invoiceNumberPosition !== "none";
        const showQR = settings.showQR && qrPosition !== "none";

        // If nothing to show, return null
        if (!showLogo && !showInvoiceNumber && !showQR) return null;

        // Helper function to render logo
        const renderLogo = () => {
          if (!showLogo) return null;
          const fitToWidth =
            !settings.useDefaultLogoSize && !!settings.logoFitToWidth;
          const logoBoxWidth = settings.useDefaultLogoSize
            ? 150
            : fitToWidth
              ? "100%"
              : settings.logoWidth || 150;
          const logoBoxHeight = settings.useDefaultLogoSize
            ? 80
            : settings.logoHeight || 80;
          return (
            <Box
              sx={{
                width: logoBoxWidth,
                maxWidth: "100%",
                height: logoBoxHeight,
                border: "1px dashed #ccc",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f5f5f5",
                flexShrink: 0,
                alignSelf: fitToWidth ? "stretch" : "auto",
                boxSizing: "border-box",
              }}
            >
              <Typography variant="caption" color="textSecondary">
                {t("logo")}
              </Typography>
            </Box>
          );
        };

        // Helper function to get heading based on type
        const getHeadingText = () => {
          if (templateType === 3) {
            // JOBCARD
            return (
              settings.jobcardHeadingPrimary ||
              settings.invoiceHeadingPrimary ||
              "JOBCARD"
            );
          } else if (templateType === 4) {
            // WORKORDER
            return (
              settings.workorderHeadingPrimary ||
              settings.invoiceHeadingPrimary ||
              "WORKORDER"
            );
          } else if (templateType === 6) {
            // DELIVERY NOTE
            return (
              settings.deliveryNoteHeadingPrimary ||
              settings.invoiceHeadingPrimary ||
              "DELIVERY NOTE"
            );
          } else if (templateType === 5) {
            // INSPECTION - show template heading instead of invoice number
            return settings.invoiceHeadingPrimary || "INSPECTION";
          } else if (isProforma) {
            return (
              settings.proformaInvoiceHeadingPrimary ||
              settings.invoiceHeadingPrimary ||
              "PROFORMA INVOICE"
            );
          }
          // INVOICE (default)
          return settings.invoiceHeadingPrimary || "INVOICE";
        };

        const getHeadingSecondary = () => {
          if (templateType === 3) {
            return (
              settings.jobcardHeadingSecondary ||
              settings.invoiceHeadingSecondary ||
              ""
            );
          } else if (templateType === 4) {
            return (
              settings.workorderHeadingSecondary ||
              settings.invoiceHeadingSecondary ||
              ""
            );
          } else if (templateType === 6) {
            return (
              settings.deliveryNoteHeadingSecondary ||
              settings.invoiceHeadingSecondary ||
              ""
            );
          } else if (isProforma) {
            return (
              settings.proformaInvoiceHeadingSecondary ||
              settings.invoiceHeadingSecondary ||
              ""
            );
          }
          return settings.invoiceHeadingSecondary || "";
        };

        // Helper function to render invoice number or template heading
        const renderInvoiceNumber = () => {
          if (!showInvoiceNumber) return null;
          const fontSize = getNumberFontSizeField() || 18;
          const fontWeight = getNumberFontWeightField() || 700;
          const isColumnDirection = settings.showInvoiceDirection === "column";

          // For inspection, show template heading instead of invoice number
          if (templateType === 5) {
            return (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "inherit",
                  flex: isColumnDirection ? "1 1 auto" : "0 0 auto",
                  maxWidth: "100%",
                }}
              >
                <Typography
                  sx={{
                    fontSize: `${fontSize}px`,
                    fontWeight: fontWeight,
                    color: "#fc5f3a",
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  {dummyData.invoiceNo}
                </Typography>
              </Box>
            );
          }

          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "inherit",
                flex: isColumnDirection ? "1 1 auto" : "0 0 auto",
                maxWidth: "100%",
              }}
            >
              <Typography
                sx={{
                  fontSize: `${fontSize}px`,
                  fontWeight: fontWeight,
                  color: "#fc5f3a",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {renderLanguage(getHeadingText(), getHeadingSecondary())}
                {isProforma ? " #: " : " # : "}
                {dummyData.invoiceNo}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  fontWeight: 500,
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {t("date")}: {formatDate(dummyData.date)}
              </Typography>
            </Box>
          );
        };

        // Helper function to render QR code
        const renderQR = () => {
          if (!showQR) return null;

          // Fixed QR pattern (8x8 grid) - deterministic pattern
          const qrPattern = [
            1, 0, 1, 0, 1, 0, 1, 0, 0, 1, 0, 1, 0, 1, 0, 1, 1, 0, 1, 0, 1, 0, 1,
            0, 0, 1, 0, 1, 0, 1, 0, 1, 1, 0, 1, 0, 1, 0, 1, 0, 0, 1, 0, 1, 0, 1,
            0, 1, 1, 0, 1, 0, 1, 0, 1, 0, 0, 1, 0, 1, 0, 1, 0, 1,
          ];

          return (
            <Box
              sx={{
                width: 100,
                height: 100,
                border: "1px dashed #ccc",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f5f5f5",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ fontSize: "0.7rem" }}
              >
                QR Code
              </Typography>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  border: "1px solid #999",
                  display: "grid",
                  gridTemplateColumns: "repeat(8, 1fr)",
                  gridTemplateRows: "repeat(8, 1fr)",
                  gap: "1px",
                  p: 0.5,
                }}
              >
                {qrPattern.map((value, i) => (
                  <Box
                    key={i}
                    sx={{
                      backgroundColor: value === 1 ? "#000" : "#fff",
                      width: "100%",
                      height: "100%",
                    }}
                  />
                ))}
              </Box>
            </Box>
          );
        };

        // Create three columns: left, center, right
        const leftItems: JSX.Element[] = [];
        const centerItems: JSX.Element[] = [];
        const rightItems: JSX.Element[] = [];
        const logoFitToWidth =
          showLogo && !settings.useDefaultLogoSize && !!settings.logoFitToWidth;

        // Process logo - independent positioning (skip columns when fit-to-width;
        // full-width logo is rendered on its own row so it is not shrink-wrapped)
        if (showLogo && !logoFitToWidth) {
          const logoEl = renderLogo();
          if (logoEl) {
            const logoBox = <Box key="logo">{logoEl}</Box>;
            if (logoPosition === "left") leftItems.push(logoBox);
            else if (logoPosition === "center") centerItems.push(logoBox);
            else if (logoPosition === "right") rightItems.push(logoBox);
          }
        }

        // Process invoice number - independent positioning
        if (showInvoiceNumber) {
          const invoiceEl = renderInvoiceNumber();
          if (invoiceEl) {
            const invoiceBox = <Box key="invoice">{invoiceEl}</Box>;
            if (invoiceNumberPosition === "left") leftItems.push(invoiceBox);
            else if (invoiceNumberPosition === "center")
              centerItems.push(invoiceBox);
            else if (invoiceNumberPosition === "right")
              rightItems.push(invoiceBox);
          }
        }

        // Process QR code - independent positioning
        if (showQR) {
          const qrEl = renderQR();
          if (qrEl) {
            const qrBox = <Box key="qr">{qrEl}</Box>;
            if (qrPosition === "left") leftItems.push(qrBox);
            else if (qrPosition === "center") centerItems.push(qrBox);
            else if (qrPosition === "right") rightItems.push(qrBox);
          }
        }

        const logoFullWidthRow = logoFitToWidth ? (
          <Box key="logo-fit-width" sx={{ width: "100%", mb: 1 }}>
            {renderLogo()}
          </Box>
        ) : null;

        return settings.showInvoiceDirection === "column" ? (
          <Box
            key="logo"
            sx={{
              display: "flex",
              flexDirection: "column",
              mb: 2,
              gap: 1,
              width: "100%",
              maxWidth: "785px",
            }}
          >
            {logoFullWidthRow}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 2,
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 1,
                }}
              >
                {leftItems}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {centerItems}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: 1,
                }}
              >
                {rightItems}
              </Box>
            </Box>
          </Box>
        ) : (
          <Box
            key="logo"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              mb: 2,
              width: "100%",
              maxWidth: "785px",
            }}
          >
            {logoFitToWidth ? (
              logoFullWidthRow
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                  width: "100%",
                  justifyContent:
                    settings.logoPosition === "left"
                      ? "flex-start"
                      : settings.logoPosition === "center"
                        ? "center"
                        : "flex-end",
                }}
              >
                {renderLogo()}
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                justifyContent: showQR ? "space-between" : "flex-start",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  marginLeft:
                    settings.invoiceNumberPosition === "center"
                      ? "auto"
                      : settings.invoiceNumberPosition === "right"
                        ? "auto"
                        : 0,
                  marginRight:
                    settings.invoiceNumberPosition === "center" ? "auto" : 0,
                }}
              >
                {renderInvoiceNumber()}
              </Box>

              {showQR && <Box>{renderQR()}</Box>}
            </Box>
          </Box>
        );
      }

      case "header": {
        if (!settings.showHeader) return null;
        return (
          <Box key="header" sx={{ mb: 3 }}>
            {/* Company and Customer Info in Table */}
            {(settings.showCompanySection || settings.showCustomerSection) && (
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table
                  size="small"
                  sx={{ border: "1px solid #ddd", borderCollapse: "collapse" }}
                >
                  <TableBody>
                    {(() => {
                      const companyFieldsList = settings.showCompanySection
                        ? Object.entries(settings.companyFields || {})
                            .filter(([, val]) => val)
                            .map(([key]) => key)
                        : [];
                      const customerFieldsList = Object.entries(
                        settings.customerFields || {},
                      )
                        .filter(([, val]) => val)
                        .map(([key]) => key);

                      const maxRows = Math.max(
                        companyFieldsList.length,
                        customerFieldsList.length,
                      );
                      const rows = [];

                      for (let i = 0; i < maxRows; i++) {
                        const companyField = companyFieldsList[i];
                        const customerField = customerFieldsList[i];

                        rows.push(
                          <TableRow key={i}>
                            {settings.showCompanySection && (
                              <TableCell
                                sx={{
                                  border: "1px solid #ddd",
                                  borderRight: settings.showCustomerSection
                                    ? "1px solid #ddd"
                                    : "1px solid #ddd",
                                  width: settings.showCustomerSection
                                    ? "50%"
                                    : "100%",
                                  py: 1,
                                }}
                              >
                                {companyField && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      width: "100%",
                                    }}
                                  >
                                    {/* Primary Label - Left */}
                                    <span
                                      style={{
                                        fontWeight: 500,
                                        flex: "0 0 auto",
                                      }}
                                    >
                                      {settings.languageDisplay !==
                                        "secondary" &&
                                        `${getLabel(companyField, "company").primary}:`}
                                    </span>
                                    {/* Value - Center */}
                                    <span
                                      style={{
                                        flex: "1 1 auto",
                                        textAlign: "center",
                                      }}
                                    >
                                      {
                                        dummyData.company[
                                          companyField as keyof typeof dummyData.company
                                        ]
                                      }
                                    </span>
                                    {/* Secondary Label - Right */}
                                    {(settings.languageDisplay === "both" ||
                                      settings.languageDisplay ===
                                        "secondary") && (
                                      <span
                                        className={
                                          secondaryLanguage === "arabic" ||
                                          secondaryLanguage === "urdu"
                                            ? "ff-arabic"
                                            : ""
                                        }
                                        style={{
                                          fontWeight: 500,
                                          flex: "0 0 auto",
                                        }}
                                      >
                                        :
                                        {
                                          getLabel(companyField, "company")
                                            .secondary
                                        }
                                      </span>
                                    )}
                                  </Box>
                                )}
                              </TableCell>
                            )}
                            {settings.showCustomerSection && (
                              <TableCell
                                sx={{
                                  border: "1px solid #ddd",
                                  width: settings.showCompanySection
                                    ? "50%"
                                    : "100%",
                                  py: 1,
                                }}
                              >
                                {customerField && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      width: "100%",
                                    }}
                                  >
                                    {/* Primary Label - Left */}
                                    <span
                                      style={{
                                        fontWeight: 500,
                                        flex: "0 0 auto",
                                      }}
                                    >
                                      {settings.languageDisplay !==
                                        "secondary" &&
                                        `${getLabel(customerField, "customer").primary}:`}
                                    </span>
                                    {/* Value - Center */}
                                    <span
                                      style={{
                                        flex: "1 1 auto",
                                        textAlign: "center",
                                      }}
                                    >
                                      {
                                        dummyData.customer[
                                          customerField as keyof typeof dummyData.customer
                                        ]
                                      }
                                    </span>
                                    {/* Secondary Label - Right */}
                                    {(settings.languageDisplay === "both" ||
                                      settings.languageDisplay ===
                                        "secondary") && (
                                      <span
                                        className={
                                          secondaryLanguage === "arabic" ||
                                          secondaryLanguage === "urdu"
                                            ? "ff-arabic"
                                            : ""
                                        }
                                        style={{
                                          fontWeight: 500,
                                          flex: "0 0 auto",
                                        }}
                                      >
                                        :
                                        {
                                          getLabel(customerField, "customer")
                                            .secondary
                                        }
                                      </span>
                                    )}
                                  </Box>
                                )}
                              </TableCell>
                            )}
                          </TableRow>,
                        );
                      }
                      return rows;
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        );
      }

      case "vehicleInfo": {
        if (isProforma) return null;
        // Hide vehicle info for consolidated invoices
        if (
          settings.invoiceType === "consolidated" ||
          !settings.showVehicleInfo
        )
          return null;
        const vehicleFields = Object.entries(
          settings.vehicleFields || {},
        ).filter(([, val]) => val);
        if (vehicleFields.length === 0) return null;

        return (
          <Box key="vehicleInfo" sx={{ mb: 3 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                variant="h6"
                sx={{ mb: 1, fontWeight: "bold", color: "#4c0055" }}
              >
                {dummyData.vehicle.make}-{dummyData.vehicle.model}
              </Typography>

              <Typography
                variant="h6"
                sx={{ mb: 1, fontWeight: "bold", color: "#4c0055" }}
              >
                {t("vehiclePlateNo")}
              </Typography>
            </div>
            <TableContainer component={Paper}>
              <Table
                size="small"
                sx={{ border: "1px solid #ddd", borderCollapse: "collapse" }}
              >
                <TableBody>
                  {(() => {
                    const rows = [];
                    for (let i = 0; i < vehicleFields.length; i += 2) {
                      const field1 = vehicleFields[i];
                      const field2 = vehicleFields[i + 1];
                      const label1 = getLabel(field1[0], "vehicle");
                      const value1 =
                        dummyData.vehicle[
                          field1[0] as keyof typeof dummyData.vehicle
                        ] || "";

                      rows.push(
                        <TableRow key={i}>
                          <TableCell
                            sx={{
                              border: "1px solid #ddd",
                              width: "50%",
                              py: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "100%",
                              }}
                            >
                              {/* Primary Label - Left */}
                              <span
                                style={{
                                  fontWeight: 500,
                                  flex: "0 0 auto",
                                }}
                              >
                                {settings.languageDisplay !== "secondary" &&
                                  `${label1.primary}:`}
                              </span>
                              {/* Value - Center */}
                              <span
                                style={{
                                  flex: "1 1 auto",
                                  textAlign: "center",
                                }}
                              >
                                {value1}
                              </span>
                              {/* Secondary Label - Right */}
                              {(settings.languageDisplay === "both" ||
                                settings.languageDisplay === "secondary") && (
                                <span
                                  className={
                                    secondaryLanguage === "arabic" ||
                                    secondaryLanguage === "urdu"
                                      ? "ff-arabic"
                                      : ""
                                  }
                                  style={{
                                    fontWeight: 500,
                                    flex: "0 0 auto",
                                  }}
                                >
                                  :{label1.secondary}
                                </span>
                              )}
                            </Box>
                          </TableCell>
                          {field2 && (
                            <TableCell
                              sx={{
                                border: "1px solid #ddd",
                                width: "50%",
                                py: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  width: "100%",
                                }}
                              >
                                {/* Primary Label - Left */}
                                <span
                                  style={{
                                    fontWeight: 500,
                                    flex: "0 0 auto",
                                  }}
                                >
                                  {settings.languageDisplay !== "secondary" &&
                                    `${getLabel(field2[0], "vehicle").primary}:`}
                                </span>
                                {/* Value - Center */}
                                <span
                                  style={{
                                    flex: "1 1 auto",
                                    textAlign: "center",
                                  }}
                                >
                                  {
                                    dummyData.vehicle[
                                      field2[0] as keyof typeof dummyData.vehicle
                                    ]
                                  }
                                </span>
                                {/* Secondary Label - Right */}
                                {(settings.languageDisplay === "both" ||
                                  settings.languageDisplay === "secondary") && (
                                  <span
                                    className={
                                      secondaryLanguage === "arabic" ||
                                      secondaryLanguage === "urdu"
                                        ? "ff-arabic"
                                        : ""
                                    }
                                    style={{
                                      fontWeight: 500,
                                      flex: "0 0 auto",
                                    }}
                                  >
                                    :{getLabel(field2[0], "vehicle").secondary}
                                  </span>
                                )}
                              </Box>
                            </TableCell>
                          )}
                        </TableRow>,
                      );
                    }
                    return rows;
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      }

      case "table": {
        // Hide table for inspection type
        if (templateType === 5) return null;
        if (!settings.showTable) return null;

        // Consolidated Invoice Table Rendering
        if (settings.invoiceType === "consolidated") {
          const getConsolidatedTableColumnLabelValue = (column: string) => {
            const customLabels =
              settings.consolidatedTableColumnLabels?.[
                column as keyof typeof settings.consolidatedTableColumnLabels
              ];
            const defaultLabels = getConsolidatedTableColumnLabel(column);

            return {
              primary:
                customLabels?.primary && customLabels.primary.trim() !== ""
                  ? customLabels.primary
                  : defaultLabels.primary,
              secondary:
                customLabels?.secondary && customLabels.secondary.trim() !== ""
                  ? customLabels.secondary
                  : defaultLabels.secondary,
            };
          };

          const isConsolidatedColumnVisible = (key: string) => {
            const cols = settings.consolidatedTableColumns;
            if (!cols) return false;
            // Prefer `total`; treat legacy `totalWithTax` as Total if `total` is unset
            if (key === "total") {
              if (cols.total != null) return !!cols.total;
              return !!cols.totalWithTax;
            }
            if (key === "totalWithTax") return false;
            return !!cols[key as keyof typeof cols];
          };

          const renderConsolidatedTableHeader = () => {
            const columns = [
              {
                key: "serialNo",
                label: getConsolidatedTableColumnLabelValue("serialNo"),
              },
              {
                key: "vehicleName",
                label: getConsolidatedTableColumnLabelValue("vehicleName"),
              },
              {
                key: "vehiclePlateNo",
                label: getConsolidatedTableColumnLabelValue("vehiclePlateNo"),
              },
              {
                key: "deliveryNoteNo",
                label: getConsolidatedTableColumnLabelValue("deliveryNoteNo"),
              },
              {
                key: "subtotal",
                label: getConsolidatedTableColumnLabelValue("subtotal"),
              },
              {
                key: "discount",
                label: getConsolidatedTableColumnLabelValue("discount"),
              },
              {
                key: "discountedSubtotal",
                label:
                  getConsolidatedTableColumnLabelValue("discountedSubtotal"),
              },
              {
                key: "tax",
                label: getConsolidatedTableColumnLabelValue("tax"),
              },
              {
                key: "total",
                label: getConsolidatedTableColumnLabelValue("total"),
              },
            ];

            return (
              <TableRow>
                {columns.map(({ key, label }) => {
                  if (!isConsolidatedColumnVisible(key)) {
                    return null;
                  }
                  return (
                    <TableCell
                      key={key}
                      sx={{
                        border: "1px solid #ddd",
                        fontWeight: "bold",
                        backgroundColor: "#f5f5f5",
                        textAlign: [
                          "subtotal",
                          "discount",
                          "discountedSubtotal",
                          "tax",
                          "total",
                        ].includes(key)
                          ? "right"
                          : "left",
                      }}
                    >
                      {renderLabel(label.primary, label.secondary)}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          };

          const renderConsolidatedTableRow = (
            note: (typeof dummyData.deliveryNotes)[0],
            index: number,
          ) => {
            const columns = [
              { key: "serialNo", value: index + 1 },
              { key: "vehicleName", value: note.vehicleName },
              { key: "vehiclePlateNo", value: note.vehiclePlateNo },
              { key: "deliveryNoteNo", value: note.deliveryNoteNo },
              { key: "subtotal", value: note.subtotal.toFixed(2) },
              { key: "discount", value: note.discount.toFixed(2) },
              {
                key: "discountedSubtotal",
                value: (note.subtotal - note.discount).toFixed(2),
              },
              { key: "tax", value: note.tax.toFixed(2) },
              { key: "total", value: note.total.toFixed(2) },
            ];

            return (
              <TableRow key={index}>
                {columns.map(({ key, value }) => {
                  if (!isConsolidatedColumnVisible(key)) {
                    return null;
                  }
                  return (
                    <TableCell
                      key={key}
                      sx={{
                        border: "1px solid #ddd",
                        textAlign: [
                          "subtotal",
                          "discount",
                          "discountedSubtotal",
                          "tax",
                          "total",
                        ].includes(key)
                          ? "right"
                          : "left",
                      }}
                    >
                      {value}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          };

          return (
            <Box key="table" sx={{ mb: 3 }}>
              <TableContainer component={Paper}>
                <Table
                  size="small"
                  sx={{ border: "1px solid #ddd", borderCollapse: "collapse" }}
                >
                  <TableHead>{renderConsolidatedTableHeader()}</TableHead>
                  <TableBody>
                    {dummyData.deliveryNotes.map((note, index) =>
                      renderConsolidatedTableRow(note, index),
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        }

        // Normal Invoice Table Rendering
        const renderItemContent = (item: (typeof dummyData.items)[0]) => {
          // Use new itemDisplayOptions if available, otherwise fall back to descriptionDisplay for backward compatibility
          const itemOptions = settings.itemDisplayOptions || {
            showTitle:
              settings.descriptionDisplay === "title" ||
              settings.descriptionDisplay === "all" ||
              !settings.descriptionDisplay,
            showCode:
              settings.descriptionDisplay === "code" ||
              settings.descriptionDisplay === "all" ||
              !settings.descriptionDisplay,
            showDescription:
              settings.descriptionDisplay === "description" ||
              settings.descriptionDisplay === "all",
          };

          const showTitle = itemOptions.showTitle ?? true;
          const showCode = itemOptions.showCode ?? true;
          const showDescription = itemOptions.showDescription ?? false;

          // Build item text parts
          const itemParts: string[] = [];
          if (showTitle && showCode) {
            itemParts.push(`${item.title} - ${item.code}`);
          } else if (showTitle) {
            itemParts.push(item.title);
          } else if (showCode) {
            itemParts.push(item.code);
          }

          // If we have item parts and description, show them on separate lines
          if (itemParts.length > 0 && showDescription && item.description) {
            return (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography variant="body2">{itemParts.join(" ")}</Typography>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.85rem", color: "#666" }}
                >
                  {item.description}
                </Typography>
              </Box>
            );
          }

          // If only description is selected
          if (!showTitle && !showCode && showDescription) {
            return item.description || "";
          }

          // Return item parts (title and/or code)
          return itemParts.join(" ") || "";
        };

        const getTableColumnLabelValue = (column: string) => {
          const customLabels =
            settings.tableColumnLabels?.[
              column as keyof typeof settings.tableColumnLabels
            ];
          const defaultLabels = getTableColumnLabel(column);

          return {
            primary:
              customLabels?.primary && customLabels.primary.trim() !== ""
                ? customLabels.primary
                : defaultLabels.primary,
            secondary:
              customLabels?.secondary && customLabels.secondary.trim() !== ""
                ? customLabels.secondary
                : defaultLabels.secondary,
          };
        };

        const renderTableHeader = () => {
          const serialNoLabel = getTableColumnLabelValue("serialNo");
          const itemLabel = getTableColumnLabelValue("item");
          const quantityLabel = getTableColumnLabelValue("quantity");
          const unitPriceLabel = getTableColumnLabelValue("unitPrice");
          const amountLabel = getTableColumnLabelValue("amount");
          const discountLabel = getTableColumnLabelValue("discount");
          const discountedSubtotalLabel =
            getTableColumnLabelValue("discountedSubtotal");
          const taxLabel = getTableColumnLabelValue("tax");
          const totalWithTaxLabel = { primary: "Total", secondary: "الإجمالي" };

          return (
            <TableRow>
              {settings.tableColumns?.serialNo && (
                <TableCell
                  sx={{
                    border: "1px solid #ddd",
                    fontWeight: "bold",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  {renderLabel(serialNoLabel.primary, serialNoLabel.secondary)}
                </TableCell>
              )}
              {settings.tableColumns?.item && (
                <TableCell
                  sx={{
                    border: "1px solid #ddd",
                    fontWeight: "bold",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  {renderLabel(itemLabel.primary, itemLabel.secondary)}
                </TableCell>
              )}
              {settings.tableColumns?.quantity && (
                <TableCell
                  sx={{
                    border: "1px solid #ddd",
                    fontWeight: "bold",
                    textAlign: "center",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  {renderLabel(quantityLabel.primary, quantityLabel.secondary)}
                </TableCell>
              )}
              {settings.tableColumns?.unitPrice && (
                <TableCell
                  sx={{
                    border: "1px solid #ddd",
                    fontWeight: "bold",
                    textAlign: "right",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  {renderLabel(
                    unitPriceLabel.primary,
                    unitPriceLabel.secondary,
                  )}
                </TableCell>
              )}
              {settings.tableColumns?.amount && (
                <TableCell
                  sx={{
                    border: "1px solid #ddd",
                    fontWeight: "bold",
                    textAlign: "right",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  {renderLabel(amountLabel.primary, amountLabel.secondary)}
                </TableCell>
              )}
              {settings.tableColumns?.discount && (
                <TableCell
                  sx={{
                    border: "1px solid #ddd",
                    fontWeight: "bold",
                    textAlign: "right",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  {renderLabel(discountLabel.primary, discountLabel.secondary)}
                </TableCell>
              )}
              {settings.tableColumns?.discountedSubtotal && (
                <TableCell
                  sx={{
                    border: "1px solid #ddd",
                    fontWeight: "bold",
                    textAlign: "right",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  {renderLabel(
                    discountedSubtotalLabel.primary,
                    discountedSubtotalLabel.secondary,
                  )}
                </TableCell>
              )}
              {settings.tableColumns?.tax && (
                <TableCell
                  sx={{
                    border: "1px solid #ddd",
                    fontWeight: "bold",
                    textAlign: "right",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  {renderLabel(taxLabel.primary, taxLabel.secondary)}
                </TableCell>
              )}
              {settings.tableColumns?.totalWithTax && (
                <TableCell
                  sx={{
                    border: "1px solid #ddd",
                    fontWeight: "bold",
                    textAlign: "right",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  {renderLabel(
                    totalWithTaxLabel.primary,
                    totalWithTaxLabel.secondary,
                  )}
                </TableCell>
              )}
            </TableRow>
          );
        };

        const getSerialNumber = (
          item: (typeof dummyData.items)[0],
          index: number,
        ) => {
          // Type indicators: ① for item, Ⓢ for service, Ⓟ for package
          const typeIndicator =
            item.type === "item" ? "Ⓘ" : item.type === "service" ? "Ⓢ" : "Ⓟ";
          return `${typeIndicator} ${index + 1}`;
        };

        const renderTableRow = (
          item: (typeof dummyData.items)[0],
          index: number,
        ) => {
          const baseAmount = item.unitPrice * item.quantity;
          const discountedSubtotal = baseAmount - item.discount;
          const totalWithTax = discountedSubtotal + item.tax;

          return (
            <TableRow key={index}>
              {settings.tableColumns?.serialNo && (
                <TableCell sx={{ border: "1px solid #ddd" }}>
                  {getSerialNumber(item, index)}
                </TableCell>
              )}
              {settings.tableColumns?.item && (
                <TableCell sx={{ border: "1px solid #ddd" }}>
                  {renderItemContent(item)}
                </TableCell>
              )}
              {settings.tableColumns?.quantity && (
                <TableCell
                  sx={{ border: "1px solid #ddd", textAlign: "center" }}
                >
                  {item.quantity}{" "}
                  {item.type === "service"
                    ? "Service(s)"
                    : item.type === "package"
                      ? "Package(s)"
                      : "Box"}
                </TableCell>
              )}
              {settings.tableColumns?.unitPrice && (
                <TableCell
                  sx={{ border: "1px solid #ddd", textAlign: "right" }}
                >
                  {item.unitPrice.toFixed(2)}
                </TableCell>
              )}
              {settings.tableColumns?.amount && (
                <TableCell
                  sx={{ border: "1px solid #ddd", textAlign: "right" }}
                >
                  {baseAmount.toFixed(2)} SAR
                </TableCell>
              )}
              {settings.tableColumns?.discount && (
                <TableCell
                  sx={{ border: "1px solid #ddd", textAlign: "right" }}
                >
                  {item.discount.toFixed(2)}
                </TableCell>
              )}
              {settings.tableColumns?.discountedSubtotal && (
                <TableCell
                  sx={{ border: "1px solid #ddd", textAlign: "right" }}
                >
                  {discountedSubtotal.toFixed(2)}
                </TableCell>
              )}
              {settings.tableColumns?.tax && (
                <TableCell
                  sx={{ border: "1px solid #ddd", textAlign: "right" }}
                >
                  {item.tax.toFixed(2)}
                </TableCell>
              )}
              {settings.tableColumns?.totalWithTax && (
                <TableCell
                  sx={{ border: "1px solid #ddd", textAlign: "right" }}
                >
                  {totalWithTax.toFixed(2)}
                </TableCell>
              )}
            </TableRow>
          );
        };

        if (settings.tableLayout === "separate") {
          // Separate tables for packages, services, items
          const packages = dummyData.items.filter(
            (item) => item.type === "package" && settings.showPackages,
          );
          const services = dummyData.items.filter(
            (item) => item.type === "service" && settings.showServices,
          );
          const items = dummyData.items.filter(
            (item) => item.type === "item" && settings.showItems,
          );

          return (
            <Box key="table" sx={{ mb: 3 }}>
              {packages.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 1, fontWeight: "bold", color: "#4c0055" }}
                  >
                    {(() => {
                      const label = getTableSectionLabel("packages");
                      return renderLabel(label.primary, label.secondary);
                    })()}
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table
                      size="small"
                      sx={{
                        border: "1px solid #ddd",
                        borderCollapse: "collapse",
                      }}
                    >
                      <TableHead>{renderTableHeader()}</TableHead>
                      <TableBody>
                        {packages.map((item, index) =>
                          renderTableRow(item, index),
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
              {services.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 1, fontWeight: "bold", color: "#4c0055" }}
                  >
                    {(() => {
                      const label = getTableSectionLabel("services");
                      return renderLabel(label.primary, label.secondary);
                    })()}
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table
                      size="small"
                      sx={{
                        border: "1px solid #ddd",
                        borderCollapse: "collapse",
                      }}
                    >
                      <TableHead>{renderTableHeader()}</TableHead>
                      <TableBody>
                        {services.map((item, index) =>
                          renderTableRow(item, index),
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
              {items.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 1, fontWeight: "bold", color: "#4c0055" }}
                  >
                    {(() => {
                      const label = getTableSectionLabel("items");
                      return renderLabel(label.primary, label.secondary);
                    })()}
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table
                      size="small"
                      sx={{
                        border: "1px solid #ddd",
                        borderCollapse: "collapse",
                      }}
                    >
                      <TableHead>{renderTableHeader()}</TableHead>
                      <TableBody>
                        {items.map((item, index) =>
                          renderTableRow(item, index),
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          );
        } else {
          // Combined table
          const filteredItems = dummyData.items.filter((item) => {
            if (item.type === "package" && !settings.showPackages) return false;
            if (item.type === "service" && !settings.showServices) return false;
            if (item.type === "item" && !settings.showItems) return false;
            return true;
          });

          if (filteredItems.length === 0) return null;

          return (
            <Box key="table" sx={{ mb: 3 }}>
              <TableContainer component={Paper}>
                <Table
                  size="small"
                  sx={{ border: "1px solid #ddd", borderCollapse: "collapse" }}
                >
                  <TableHead>{renderTableHeader()}</TableHead>
                  <TableBody>
                    {filteredItems.map((item, index) =>
                      renderTableRow(item, index),
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        }
      }

      case "summary": {
        // Hide summary for inspection type
        if (templateType === 5) return null;
        return (
          <Box
            key="summary"
            sx={{ mb: 3, display: "flex", justifyContent: "flex-end" }}
          >
            <Box sx={{ minWidth: 300 }}>
              {!isProforma && settings.showPaymentInfo && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>
                      {(() => {
                        const label = getPaymentLabel();
                        return renderLabel(label.primary, label.secondary);
                      })()}
                      :
                    </strong>{" "}
                    {dummyData.paymentMode}
                  </Typography>
                </Box>
              )}
              <TableContainer component={Paper}>
                <Table
                  size="small"
                  sx={{ border: "1px solid #ddd", borderCollapse: "collapse" }}
                >
                  <TableBody>
                    {settings.summaryItems?.subtotal && (
                      <TableRow>
                        <TableCell sx={{ border: "1px solid #ddd", py: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span style={{ fontWeight: 500 }}>
                              {(() => {
                                const customLabels =
                                  settings.summaryLabels?.subtotal;
                                const defaultLabels =
                                  getSummaryLabel("subtotal");
                                const label = {
                                  primary:
                                    customLabels?.primary &&
                                    customLabels.primary.trim() !== ""
                                      ? customLabels.primary
                                      : defaultLabels.primary,
                                  secondary:
                                    customLabels?.secondary &&
                                    customLabels.secondary.trim() !== ""
                                      ? customLabels.secondary
                                      : defaultLabels.secondary,
                                };
                                return renderLabel(
                                  label.primary,
                                  label.secondary,
                                );
                              })()}
                              :
                            </span>
                            <span>{dummyData.subtotal.toFixed(2)}</span>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                    {settings.summaryItems?.discount && (
                      <TableRow>
                        <TableCell sx={{ border: "1px solid #ddd", py: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span style={{ fontWeight: 500 }}>
                              {(() => {
                                const customLabels =
                                  settings.summaryLabels?.discount;
                                const defaultLabels =
                                  getSummaryLabel("discount");
                                const label = {
                                  primary:
                                    customLabels?.primary &&
                                    customLabels.primary.trim() !== ""
                                      ? customLabels.primary
                                      : defaultLabels.primary,
                                  secondary:
                                    customLabels?.secondary &&
                                    customLabels.secondary.trim() !== ""
                                      ? customLabels.secondary
                                      : defaultLabels.secondary,
                                };
                                return renderLabel(
                                  label.primary,
                                  label.secondary,
                                );
                              })()}
                              :
                            </span>
                            <span>{dummyData.discount.toFixed(2)}</span>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                    {settings.summaryItems?.discountedSubtotal && (
                      <TableRow>
                        <TableCell sx={{ border: "1px solid #ddd", py: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span style={{ fontWeight: 500 }}>
                              {(() => {
                                const customLabels =
                                  settings.summaryLabels?.discountedSubtotal;
                                const defaultLabels =
                                  getSummaryLabel("discountedSubtotal");
                                const label = {
                                  primary:
                                    customLabels?.primary &&
                                    customLabels.primary.trim() !== ""
                                      ? customLabels.primary
                                      : defaultLabels.primary,
                                  secondary:
                                    customLabels?.secondary &&
                                    customLabels.secondary.trim() !== ""
                                      ? customLabels.secondary
                                      : defaultLabels.secondary,
                                };
                                return renderLabel(
                                  label.primary,
                                  label.secondary,
                                );
                              })()}
                              :
                            </span>
                            <span>
                              {(
                                dummyData.subtotal - dummyData.discount
                              ).toFixed(2)}
                            </span>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                    {settings.summaryItems?.tax && (
                      <TableRow>
                        <TableCell sx={{ border: "1px solid #ddd", py: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span style={{ fontWeight: 500 }}>
                              {(() => {
                                const customLabels =
                                  settings.summaryLabels?.tax;
                                const defaultLabels = getSummaryLabel("tax");
                                const label = {
                                  primary:
                                    customLabels?.primary &&
                                    customLabels.primary.trim() !== ""
                                      ? customLabels.primary
                                      : defaultLabels.primary,
                                  secondary:
                                    customLabels?.secondary &&
                                    customLabels.secondary.trim() !== ""
                                      ? customLabels.secondary
                                      : defaultLabels.secondary,
                                };
                                return renderLabel(
                                  label.primary,
                                  label.secondary,
                                );
                              })()}
                              :
                            </span>
                            <span>{dummyData.tax.toFixed(2)}</span>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                    {settings.summaryItems?.total && (
                      <TableRow>
                        <TableCell
                          sx={{
                            border: "1px solid #ddd",
                            borderTop: "2px solid #fc5f3a",
                            fontWeight: "bold",
                            py: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span>
                              {(() => {
                                const customLabels =
                                  settings.summaryLabels?.total;
                                const defaultLabels = getSummaryLabel("total");
                                const label = {
                                  primary:
                                    customLabels?.primary &&
                                    customLabels.primary.trim() !== ""
                                      ? customLabels.primary
                                      : defaultLabels.primary,
                                  secondary:
                                    customLabels?.secondary &&
                                    customLabels.secondary.trim() !== ""
                                      ? customLabels.secondary
                                      : defaultLabels.secondary,
                                };
                                return renderLabel(
                                  label.primary,
                                  label.secondary,
                                );
                              })()}
                              :
                            </span>
                            <span>{dummyData.total.toFixed(2)}</span>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                    {!isProforma && settings.showPaymentInfo && (
                      <>
                        <TableRow>
                          <TableCell sx={{ border: "1px solid #ddd", py: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <span style={{ fontWeight: 500 }}>
                                {(() => {
                                  const customLabels =
                                    settings.summaryLabels?.creditAmount;
                                  const defaultLabels =
                                    getSummaryLabel("creditAmount");
                                  const label = {
                                    primary:
                                      customLabels?.primary &&
                                      customLabels.primary.trim() !== ""
                                        ? customLabels.primary
                                        : defaultLabels.primary,
                                    secondary:
                                      customLabels?.secondary &&
                                      customLabels.secondary.trim() !== ""
                                        ? customLabels.secondary
                                        : defaultLabels.secondary,
                                  };
                                  return renderLabel(
                                    label.primary,
                                    label.secondary,
                                  );
                                })()}
                                :
                              </span>
                              <span>{dummyData.creditAmount.toFixed(2)}</span>
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ border: "1px solid #ddd", py: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <span style={{ fontWeight: 500 }}>
                                {(() => {
                                  const customLabels =
                                    settings.summaryLabels?.paidAmount;
                                  const defaultLabels =
                                    getSummaryLabel("paidAmount");
                                  const label = {
                                    primary:
                                      customLabels?.primary &&
                                      customLabels.primary.trim() !== ""
                                        ? customLabels.primary
                                        : defaultLabels.primary,
                                    secondary:
                                      customLabels?.secondary &&
                                      customLabels.secondary.trim() !== ""
                                        ? customLabels.secondary
                                        : defaultLabels.secondary,
                                  };
                                  return renderLabel(
                                    label.primary,
                                    label.secondary,
                                  );
                                })()}
                                :
                              </span>
                              <span>{dummyData.paidAmount.toFixed(2)}</span>
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ border: "1px solid #ddd", py: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <span style={{ fontWeight: 500 }}>
                                {(() => {
                                  const customLabels =
                                    settings.summaryLabels?.dueAmount;
                                  const defaultLabels =
                                    getSummaryLabel("dueAmount");
                                  const label = {
                                    primary:
                                      customLabels?.primary &&
                                      customLabels.primary.trim() !== ""
                                        ? customLabels.primary
                                        : defaultLabels.primary,
                                    secondary:
                                      customLabels?.secondary &&
                                      customLabels.secondary.trim() !== ""
                                        ? customLabels.secondary
                                        : defaultLabels.secondary,
                                  };
                                  return renderLabel(
                                    label.primary,
                                    label.secondary,
                                  );
                                })()}
                                :
                              </span>
                              <span>{dummyData.dueAmount.toFixed(2)}</span>
                            </Box>
                          </TableCell>
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {!isProforma && settings.showPaymentInfo && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>
                      {(() => {
                        const label = getPaymentLabel();
                        return renderLabel(label.primary, label.secondary);
                      })()}
                      :
                    </strong>{" "}
                    PAY-2025-10-709 - {dummyData.paymentMode} -{" "}
                    {formatDate(dummyData.date)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        );
      }

      case "footer": {
        if (!settings.showFooter) return null;

        const footerItems = settings.footerItems || {};
        const hasFooterItems = Object.values(footerItems).some((val) => val);
        const hasFooterText =
          settings.footerText && settings.footerText.trim() !== "";
        const brandingFooterText =
          branding?.attributes?.footerText?.trim() || "";
        const brandingAboutHref = getBrandingAboutHref(
          branding?.attributes?.about,
        );

        if (!hasFooterItems && !hasFooterText && !brandingFooterText)
          return null;

        const activeFooterItems = [];
        if (footerItems.address)
          activeFooterItems.push(dummyData.company.address);
        if (footerItems.email) activeFooterItems.push(dummyData.company.email);
        if (footerItems.phone) activeFooterItems.push(dummyData.company.phone);
        if (footerItems.socialLinks)
          activeFooterItems.push("Facebook | Twitter | Instagram");

        return (
          <Box key="footer" sx={{ mt: 3, pt: 2, borderTop: "1px solid #ddd" }}>
            {hasFooterText && (
              <Typography
                variant="caption"
                align="center"
                display="block"
                sx={{ mb: hasFooterItems ? 2 : 0 }}
              >
                {t(settings?.footerText || "")}
              </Typography>
            )}

            {hasFooterItems && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1,
                  mt: hasFooterText ? 1 : 0,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.5,
                    alignItems: "center",
                    flex: 1,
                  }}
                >
                  {activeFooterItems.map((item, index) => (
                    <React.Fragment key={index}>
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "0.75rem", color: "#666" }}
                      >
                        {item}
                      </Typography>
                      {index < activeFooterItems.length - 1 && (
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.75rem", color: "#999" }}
                        >
                          |
                        </Typography>
                      )}
                    </React.Fragment>
                  ))}
                </Box>
                {footerItems.pageNumber && (
                  <Typography
                    variant="caption"
                    sx={{ fontSize: "0.75rem", color: "#666" }}
                  >
                    {(() => {
                      const label = getFooterLabel("pageNumber");
                      return `${renderLabel(label.primary, label.secondary)} 1`;
                    })()}
                  </Typography>
                )}
              </Box>
            )}
            {brandingFooterText ? (
              <Typography
                variant="caption"
                align="center"
                display="block"
                className="mt-[15px]"
              >
                {brandingAboutHref ? (
                  <a
                    href={brandingAboutHref}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: theme?.primary }}
                  >
                    {brandingFooterText}
                  </a>
                ) : (
                  brandingFooterText
                )}
              </Typography>
            ) : null}
          </Box>
        );
      }

      case "terms": {
        if (!settings.showTerms) return null;
        const termsLabel = getTermsLabel();
        const termsPrimary = termsLabel.primary;
        const termsSecondary = termsLabel.secondary;
        const termsContent =
          t("termsAndConditions") || "Terms and conditions content";

        return (
          <Box key="terms" sx={{ mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 2,
              }}
            >
              {/* Primary Language Side */}
              {settings.languageDisplay !== "secondary" && (
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    {termsPrimary}
                  </Typography>
                  <Typography variant="body2">{termsContent}</Typography>
                </Box>
              )}
              {/* Secondary Language Side */}
              {settings.languageDisplay !== "primary" && (
                <Box
                  sx={{
                    flex: 1,
                    textAlign:
                      settings.languageDisplay === "secondary"
                        ? "left"
                        : "right",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold", mb: 1 }}
                    className={
                      secondaryLanguage === "arabic" ||
                      secondaryLanguage === "urdu"
                        ? "ff-arabic"
                        : ""
                    }
                  >
                    {termsSecondary}
                  </Typography>
                  <Typography
                    variant="body2"
                    className={
                      secondaryLanguage === "arabic" ||
                      secondaryLanguage === "urdu"
                        ? "ff-arabic"
                        : ""
                    }
                  >
                    {termsContent}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        );
      }

      case "inspectionItems": {
        if (templateType !== 5) return null;
        // Default to true for inspection type if not set
        if (settings.showInspectionItems === false) return null;
        // Use dummy data instead of settings.inspectionItems
        const items = dummyData.inspectionItems || [];
        if (items.length === 0) return null;

        const getStatusColor = (status?: string) => {
          switch (status) {
            case "working":
              return "#4caf50"; // Green
            case "needRepair":
              return "#ff9800"; // Orange
            case "brokenOrMissing":
              return "#f44336"; // Red
            default:
              return "#666";
          }
        };

        const getStatusLabel = (status?: string) => {
          switch (status) {
            case "working":
              return t("working");
            case "needRepair":
              return t("needRepair");
            case "brokenOrMissing":
              return t("brokenOrMissing");
            default:
              return status || "";
          }
        };

        // Two-column layout: Services on left, Vehicle image on right
        return (
          <Box key="inspectionItems" sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              sx={{ mb: 2, fontWeight: "bold", color: "#4c0055" }}
            >
              {t("section")} 1
            </Typography>
            <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
              {/* Left Column - Services Table */}
              <Box sx={{ flex: 1 }}>
                <TableContainer component={Paper}>
                  <Table
                    size="small"
                    sx={{
                      border: "1px solid #ddd",
                      borderCollapse: "collapse",
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            border: "1px solid #ddd",
                            fontWeight: "bold",
                            backgroundColor: "#f5f5f5",
                          }}
                        >
                          #
                        </TableCell>
                        <TableCell
                          sx={{
                            border: "1px solid #ddd",
                            fontWeight: "bold",
                            backgroundColor: "#f5f5f5",
                          }}
                        >
                          {t("partName")}
                        </TableCell>
                        <TableCell
                          sx={{
                            border: "1px solid #ddd",
                            fontWeight: "bold",
                            backgroundColor: "#f5f5f5",
                          }}
                        >
                          {t("status")}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((item, index) => (
                        <TableRow key={item.id || index}>
                          <TableCell sx={{ border: "1px solid #ddd" }}>
                            {index + 1}
                          </TableCell>
                          <TableCell sx={{ border: "1px solid #ddd" }}>
                            {item.partName || `Part ${index + 1}`}
                          </TableCell>
                          <TableCell sx={{ border: "1px solid #ddd" }}>
                            <Typography
                              variant="body2"
                              sx={{ color: getStatusColor(item.status) }}
                            >
                              {getStatusLabel(item.status)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Right Column - Vehicle Image with Marked Points */}
              {settings.showVehicleImage !== false && (
                <Box sx={{ flex: 1, minWidth: 300 }}>
                  <Box
                    sx={{
                      width: "100%",
                      height: 250,
                      border: "2px solid #ddd",
                      borderRadius: 1,
                      position: "relative",
                      backgroundColor: "#f9f9f9",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}
                  >
                    {/* Vehicle SVG Image */}
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src="/inspection-images/sedan.svg"
                        alt="Vehicle"
                        style={{
                          width: "80%",
                          height: "80%",
                          objectFit: "contain",
                        }}
                        onError={(e) => {
                          // Fallback if image doesn't exist - show placeholder
                          const target = e.target as HTMLImageElement;
                          target.style.display = "none";
                          const fallback = document.createElement("div");
                          fallback.textContent = "Vehicle Image";
                          fallback.style.cssText =
                            "color: #999; font-size: 11px;";
                          target.parentElement?.appendChild(fallback);
                        }}
                      />
                      {/* Marked Points on Vehicle */}
                      {[1, 2, 3, 4].map((pointNum) => {
                        const positions = [
                          {
                            top: "20%",
                            left: "50%",
                            transform: "translateX(-50%)",
                          }, // Point 1 - Top center
                          {
                            top: "50%",
                            left: "20%",
                            transform: "translateY(-50%)",
                          }, // Point 2 - Left side
                          {
                            top: "80%",
                            left: "50%",
                            transform: "translateX(-50%)",
                          }, // Point 3 - Bottom center
                          {
                            top: "50%",
                            right: "20%",
                            transform: "translateY(-50%)",
                          }, // Point 4 - Right side
                        ];
                        const pos = positions[pointNum - 1];
                        return (
                          <Box
                            key={pointNum}
                            sx={{
                              position: "absolute",
                              ...pos,
                              width: 24,
                              height: 24,
                              borderRadius: "50%",
                              backgroundColor: "#ff4444",
                              border: "2px solid #fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              zIndex: 10,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#fff",
                                fontWeight: "bold",
                                fontSize: "9px",
                              }}
                            >
                              {pointNum}
                            </Typography>
                          </Box>
                        );
                      })}
                      {/* Highlighted Area (Red box around front section) */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: "10%",
                          left: "20%",
                          width: "60%",
                          height: "30%",
                          border: "3px solid #f44336",
                          borderRadius: "4px",
                          backgroundColor: "rgba(244, 67, 54, 0.1)",
                          zIndex: 5,
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        );
      }

      case "vehicleImage": {
        // Vehicle image is now integrated into inspectionItems section
        // This case is kept for backward compatibility but returns null for inspection type
        if (templateType === 5) return null;
        if (!settings.showVehicleImage) return null;
        const position = settings.vehicleImagePosition || "right";

        if (position === "none") return null;

        return (
          <Box
            key="vehicleImage"
            sx={{
              mb: 3,
              display: "flex",
              justifyContent:
                position === "left"
                  ? "flex-start"
                  : position === "center"
                    ? "center"
                    : "flex-end",
            }}
          >
            <Box
              sx={{
                width: 300,
                height: 200,
                border: "2px dashed #ccc",
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f9f9f9",
              }}
            >
              <Typography variant="body2" color="textSecondary">
                {t("vehicleImage")}
              </Typography>
            </Box>
          </Box>
        );
      }

      case "customerSignature": {
        if (templateType !== 5) return null;
        // Default to true for inspection type if not set
        if (settings.showCustomerSignature === false) return null;

        const signatureLabel = settings.customerSignatureLabel || {
          primary: "Customer Signature",
          secondary: "توقيع العميل",
        };

        return (
          <Box key="customerSignature" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
              {renderLabel(
                signatureLabel.primary || "Customer Signature",
                signatureLabel.secondary || "توقيع العميل",
              )}
              :
            </Typography>
            <Box
              sx={{
                width: "40%",
                height: 100,
                border: "2px solid #ddd",
                borderRadius: 1,
                backgroundColor: "#fafafa",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Dummy Signature */}
              <Box
                sx={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg
                  width="200"
                  height="60"
                  style={{ opacity: 0.6 }}
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M 20 40 Q 40 20, 60 30 T 100 35 T 140 32 T 180 38"
                    stroke="#333"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                  />
                  <path
                    d="M 30 45 Q 50 25, 70 35 T 110 40 T 150 37 T 190 43"
                    stroke="#333"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                  />
                  <path
                    d="M 25 50 Q 45 30, 65 40 T 105 45 T 145 42 T 185 48"
                    stroke="#333"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
              </Box>
            </Box>
          </Box>
        );
      }

      case "inspectionRemarks": {
        if (templateType !== 5) return null;

        const showRemarks = settings.showInspectionRemarks !== false;
        const showTechnicians = settings.showTechnicians !== false;
        if (!showRemarks && !showTechnicians) return null;

        const remarksLabel = settings.inspectionRemarksLabel || {
          primary: "Remarks",
          secondary: "ملاحظات",
        };
        const techniciansLabel = settings.inspectionTechniciansLabel || {
          primary: "Technicians",
          secondary: "الفنيين",
        };

        const fieldBoxSx = {
          width: "100%",
          minHeight: 90,
          border: "2px solid #ddd",
          borderRadius: 1,
          backgroundColor: "#fafafa",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        };

        return (
          <Box key="inspectionRemarks" sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
              {showRemarks && (
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    {renderLabel(
                      remarksLabel.primary || "Remarks",
                      remarksLabel.secondary || "ملاحظات",
                    )}
                    :
                  </Typography>
                  <Box sx={fieldBoxSx}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ opacity: 0.7 }}
                    >
                      {t("remarks")}
                    </Typography>
                  </Box>
                </Box>
              )}
              {showTechnicians && (
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    {renderLabel(
                      techniciansLabel.primary || "Technicians",
                      techniciansLabel.secondary || "الفنيين",
                    )}
                    :
                  </Typography>
                  <Box
                    component="ol"
                    sx={{
                      m: 0,
                      pl: 2.5,
                      listStyleType: "decimal",
                    }}
                  >
                    {(dummyData.technicians || []).map((name, index) => (
                      <Typography
                        key={index}
                        component="li"
                        variant="body2"
                        sx={{ mb: 0.5 }}
                      >
                        {name}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        );
      }

      case "notes": {
        if (!settings.showNotes) return null;
        return (
          <Box key="notes" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
              {renderLabel(
                t("notesForCustomer"),
                t("notesForCustomerSecondary"),
              )}
            </Typography>
            <Typography variant="body2">{t("none")}</Typography>
          </Box>
        );
      }

      default:
        return null;
    }
  };

  const getSectionOrder = () => {
    if (settings.sectionOrder && settings.sectionOrder.length > 0) {
      return settings.sectionOrder;
    }
    return getDefaultSectionOrderForTemplateType(templateType);
  };

  const sectionOrder = getSectionOrder();

  const thermalPreviewData: ThermalInvoiceData = {
    invoiceNo: dummyData.invoiceNo,
    date: dummyData.date,
    company: dummyData.company,
    customer: dummyData.customer,
    vehicle: dummyData.vehicle,
    items: dummyData.items,
    deliveryNotes: dummyData.deliveryNotes,
    subtotal: dummyData.subtotal,
    discount: dummyData.discount,
    tax: dummyData.tax,
    total: dummyData.total,
    creditAmount: dummyData.creditAmount,
    paidAmount: dummyData.paidAmount,
    dueAmount: dummyData.dueAmount,
    paymentMode: dummyData.paymentMode,
    footerText: dummyData.footerText,
  };

  if (
    isInvoiceOrProformaTemplateType(templateType) &&
    isThermalPrint(settings)
  ) {
    return (
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
        }}
      >
        <Paper
          sx={{
            p: 1,
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            overflow: "hidden",
          }}
        >
          <ThermalInvoiceContent
            settings={settings}
            data={thermalPreviewData}
            theme={theme}
            templateType={templateType}
          />
        </Paper>
      </Box>
    );
  }

  // Prefer hydrated settings.designVariant ("modern"|"classic") from designVariant column (1|2)
  if ((settings.designVariant || "classic") === "modern") {
    return (
      <ModernInvoicePreview
        settings={settings}
        theme={theme}
        formData={formData}
        templateType={templateType}
      />
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        maxWidth: "900px",
        margin: "0 auto",
        backgroundColor: "#fff",
        borderRadius: "8px",
      }}
    >
      <Paper
        sx={{
          p: 4,
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        {sectionOrder.map((sectionId) => renderSection(sectionId))}
      </Paper>
    </Box>
  );
};

export default InvoicePreview;
